import { YaNative, YaNativeInstance, getYaNative } from '@yanolja-org/ya-native';
import Kinesis from 'aws-sdk/clients/kinesis';
import { CognitoIdentityCredentials } from 'aws-sdk/lib/core';
import debug from 'debug';
import Cookies from 'js-cookie';
import { filter, pipe } from 'ramda';

import { waitFor } from '../../../utils';
import { IKinesisLike, IKinesisLogFunction, ILogFields, TEventTypes } from './interfaces';
import { injectSysFields } from './sysFields';
import { sanitizeWithReport } from './utils';

enum EVENT_COLOR {
  VIEW = 'LimeGreen',
  CLICK = 'DeepPink',
  IMPR = 'Orange',
}

const log = debug('sunny:Kinesis');

let yaNative: YaNativeInstance | undefined | null;
let credentials: CognitoIdentityCredentials | undefined | null;

/**
 * yaNative 생성이 가능하면 yaNative의 kinesis 메서드를 반환.
 *
 * 일반적인 상황에선 크레덴셜 발급/갱신 후 언제나 새로운 AWS 키네시스 인스턴스의 `putRecord` 메서드 반환.
 */
export const getTracker = async (): Promise<IKinesisLike> => {
  if (yaNative === undefined) {
    try {
      yaNative = getYaNative();
    } catch (e) {
      // webview 아님
      yaNative = null;
    }
  }
  if (yaNative?.isEnabled) {
    return {
      putRecord: (data) => {
        const send = (yaNative as YaNative).kinesis.bind(yaNative, data);
        return {
          send,
          promise: () =>
            new Promise((resolve) => {
              send((_: any, result: any) => {
                resolve(result);
              });
            }),
        };
      },
    };
  }

  if (!credentials) {
    credentials = new CognitoIdentityCredentials(
      {
        IdentityPoolId: window.ya__.identityPoolID,
      },
      {
        region: 'ap-northeast-2',
      },
    );
    await credentials.getPromise();
  }

  const cgntId = Cookies.get('cgntId');

  if (!cgntId) {
    const cgntIdFromLocal = localStorage.getItem('cgntId');

    if (cgntIdFromLocal) {
      localStorage.removeItem('cgntId');
    }

    Cookies.set('cgntId', cgntIdFromLocal || credentials.identityId, {
      domain: '.yanolja.com',
      expires: 1825,
    });
  }

  const instance = new Kinesis({
    credentials,
    apiVersion: '2013-12-02',
    region: 'ap-northeast-2',
  });

  return {
    // 브라우저에서 추적 시 웹 전용 시스템 필드 주입
    putRecord: pipe(injectSysFields, instance.putRecord.bind(instance)),
  };
};

const l = (data: ILogFields): void => {
  const eventType = data.eventType;

  if (eventType === 'impr' && data.objectContainer) {
    log(`%c[${eventType}:${data.objectContainer || ''}] %O`, `color: ${EVENT_COLOR[eventType.toUpperCase()]}`, data);
  } else {
    log(`%c[${eventType}] %O`, `color: ${EVENT_COLOR[eventType.toUpperCase()]}`, data);
  }
};
/**
 * 데이터 하나를 스트림에 올린다.
 * @param data
 */
export const put = async (logFields: ILogFields): Promise<void> => {
  const data = sanitizeWithReport(logFields);

  try {
    const kinesis = await getTracker();
    if (window.ya__ && window.ya__.runMode !== 'local') {
      l(data);
      await kinesis.putRecord(data).promise();
    } else {
      log(data);
    }
  } catch (e) {
    console.error(e);
  }
};

const putEvent = (
  eventType: TEventTypes,
  pageName: string,
  data?: Omit<ILogFields, 'eventType' | 'pageName'>,
): Promise<void> =>
  put({
    pageName,
    eventType,
    ...data,
  });

/**
 * 페이지뷰 데이터 하나를 스트림에 올린다.
 * @param pageName
 * @param data
 */
export const view: IKinesisLogFunction = (pageName, data?) => waitFor(100).then(() => putEvent('view', pageName, data));

/**
 * 클릭 데이터 하나를 스트림에 올린다.
 * @param pageName
 * @param data
 */
export const click: IKinesisLogFunction = (pageName, data?) => putEvent('click', pageName, data);

/**
 * 임프레션 데이터 하나를 스트림에 올린다.
 * @param pageName
 * @param data
 */
export const impression: IKinesisLogFunction = (pageName, data?) => putEvent('impr', pageName, data);

/**
 * bg 데이터 하나를 스트림에 올린다.
 * @param pageName
 * @param data
 */
export const bg: IKinesisLogFunction = (pageName, data?) => putEvent('bg', pageName, data);

export const _resetInstance = (): void => (yaNative = undefined);

/**
 * 로그 필드 중에 값이 없으면 필드를 없애야 할 경우 쓰인다.
 * 로그 정의서에서 키값 뒤에 ?표가 있는 경우($tabName?)
 */
export const optionalLogFilter = filter((n) => n === 0 || n === false || !!n);
